import { AUTH_PATHS, PATH } from '~/constants/path';

/**
 * 인증이 끊겨 로그인 화면으로 밀려나기 직전의 주소를 담아두는 키.
 *
 * 401 → /logout → /login → (2차 인증) 순으로 여러 화면을 거쳐야 로그인이 끝나므로
 * 화면 간에 값을 물려주지 않아도 살아남는 sessionStorage를 쓴다. 탭 단위로 격리되고
 * 탭을 닫으면 사라지는 것도 이 값의 수명과 맞다.
 */
const REDIRECT_PATH_STORAGE_KEY = 'loginRedirectPath';

/** 로그인 전 단계 화면을 뺀, 복원 대상이 될 수 있는 서비스 경로 */
const RESTORABLE_PATHS: string[] = Object.values(PATH).filter((path) => !AUTH_PATHS.includes(path));

/**
 * 서비스 내부 경로와의 완전 일치로 판정한다. 외부 URL·`//host` 류가 통과하면
 * 로그인 후 외부 사이트로 보내는 오픈 리다이렉트가 되므로, 부분 일치·접두사 검사는 쓰지 않는다.
 */
const isRestorablePath = (path: string): boolean => {
  const [pathname] = path.split('?');

  return Boolean(pathname && RESTORABLE_PATHS.includes(pathname));
};

/**
 * 로그인 후 되돌아갈 주소를 남긴다. 쿼리스트링까지 함께 넘겨 조회조건이 살아나게 한다.
 *
 * 서비스 경로가 아니면 저장하지 않아 로그인 후 기존 착지 화면으로 간다.
 * 로그인 전 단계 화면을 제외하는 것은 로그인 직후 다시 로그인 화면으로 돌아가는 순환을 막기 위함이다.
 */
export const saveLoginRedirectPath = (path: string): void => {
  if (!isRestorablePath(path)) {
    return;
  }

  try {
    sessionStorage.setItem(REDIRECT_PATH_STORAGE_KEY, path);
  } catch {
    // 사이트 데이터가 차단된 브라우저에서는 복원을 포기한다. 복원 실패가 로그인 흐름을 막아선 안 된다.
  }
};

/**
 * 저장된 복원 주소를 꺼내면서 비운다. 남은 값이 없으면 `fallbackPath`로 보낸다 —
 * 기본값은 이 기능 도입 전 로그인 착지 화면인 상품 등록/수정이고, 권한별 기본 경로가
 * 있는 호출부(2단계 인증)는 그 경로를 넘긴다.
 *
 * 저장 시점에 걸렀더라도 꺼낼 때 다시 판정한다 — 검증 없는 저장 지점이 추가되거나
 * 다른 코드가 같은 키에 값을 쓰는 회귀가 생겨도 외부 주소로의 이동을 막기 위함이다.
 */
export const consumeLoginRedirectPath = (fallbackPath: string = PATH.PRODUCT_REGISTER): string => {
  try {
    const savedPath = sessionStorage.getItem(REDIRECT_PATH_STORAGE_KEY);

    sessionStorage.removeItem(REDIRECT_PATH_STORAGE_KEY);

    return savedPath && isRestorablePath(savedPath) ? savedPath : fallbackPath;
  } catch {
    return fallbackPath;
  }
};
